import { useTranslation } from "react-i18next";
import { CarCardSkeleton } from "@/components/cars/CarCard";

/**
 * Full-page skeleton for /search. Shown while:
 *   1. checkAlgoliaIndex() is round-tripping on the very first visit, and
 *   2. the lazy-loaded AlgoliaSearchPageContent chunk is downloading.
 *
 * Replaces the previous full-screen <PageLoader /> spinner so the user
 * immediately sees ghost cards in the right shape — feels much faster
 * than a centred spinner that "snaps" into populated cards.
 *
 * The visual shapes intentionally mirror the real page chrome
 * (filter sidebar, title row, rental-type pills, results grid, and the
 * right-hand map column) so there is no layout shift when the real
 * content swaps in. The split is ~55% content / ~45% map on `xl+` to
 * match the live SearchPage layout; the map column is hidden on
 * smaller breakpoints, also matching the real page.
 */
export function SearchPageSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="journey-page search-journey" role="status" aria-busy="true" aria-label={t("searchSkeleton.loading_aria")}>
      <span className="sr-only">{t("searchSkeleton.loading_ellipsis")}</span>
      <div aria-hidden="true">
      <div className="flex min-h-[calc(100vh-4rem)] min-h-[calc(100dvh-4rem)]">
        <div className="search-results-pane w-full min-w-0 overflow-y-visible md:overflow-y-auto xl:w-[55%]">
          <div className="search-results-padding px-4 py-8 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-6 lg:flex-row">
              <aside className="hidden w-72 shrink-0 lg:block" aria-hidden="true">
                <div className="journey-panel search-filter-panel sticky top-24 p-6">
                  <div className="bac-shimmer h-5 w-24 rounded-md" />
                  <div className="mt-6 space-y-5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i}>
                        <div className="bac-shimmer h-4 w-28 rounded-md" />
                        <div className="bac-shimmer mt-2 h-9 w-full rounded-full" />
                      </div>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="search-results-content min-w-0 flex-1">
                {/* Reserve the same compact search pill and quick-filter rail
                    as MobileSearchHeader; do not render pretend controls. */}
                <div className="msh-root -mx-4 mb-4 px-4 pb-2.5 pt-3 sm:-mx-6 sm:px-6 lg:hidden">
                  <div className="flex items-center gap-2.5">
                    <div className="bac-shimmer h-[52px] min-w-0 flex-1 rounded-full" />
                    <div className="bac-shimmer h-[52px] w-[52px] shrink-0 rounded-full" />
                  </div>
                  <div className="mt-2.5 flex gap-2 overflow-hidden">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="bac-shimmer h-10 w-24 shrink-0 rounded-full" />
                    ))}
                  </div>
                </div>
                <div className="search-results-header mb-6 flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0 max-w-full space-y-2">
                    <div className="bac-shimmer h-4 w-48 max-w-full rounded-md" />
                    <div className="bac-shimmer hidden h-7 w-72 max-w-full rounded-md lg:block" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bac-shimmer h-9 w-20 rounded-full" />
                    <div className="bac-shimmer h-9 w-32 rounded-full" />
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap gap-2" aria-hidden="true">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="bac-shimmer h-9 w-24 rounded-full" />
                  ))}
                </div>

                <div
                  className="search-grid-with-map grid gap-6 sm:grid-cols-2"
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <CarCardSkeleton key={i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/*
         * Right-hand map placeholder. Hidden below `xl` to mirror the
         * real SearchPage which only renders the split map column on
         * large screens (mobile uses an overlay opened from the FAB).
         * Sticky + full viewport height so the placeholder block
         * occupies exactly the same area the real map will fill, which
         * eliminates the "map disappears, then snaps back" flash on
         * cold visits to /search.
         */}
        <div
          className="hidden xl:block xl:w-[45%] xl:sticky xl:top-16 xl:h-[calc(100vh-4rem)] xl:h-[calc(100dvh-4rem)]"
          aria-hidden="true"
        >
          <div className="bac-shimmer h-full w-full" />
        </div>
      </div>
      </div>
    </div>
  );
}
